import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

import { decrementBlock, incrementBlock } from './navigationBlockStore';

/**
 * Blocks both browser-level navigation (refresh / close tab) and
 * React-Router in-app navigation while `shouldBlock` is true.
 *
 * - Browser: registers a `beforeunload` listener → native "Leave page?" dialog.
 * - Router:  uses `useBlocker` → `window.confirm` prompt on SPA route changes.
 *            In browsers that support the Navigation API (Chrome/Edge), React
 *            Router v7 also intercepts reloads. To prevent a double-dialog when
 *            the user confirms they want to leave, the `beforeunload` listener is
 *            removed before calling `blocker.proceed()`.
 * - Store:   registers with `navigationBlockStore` so external code (e.g. logout)
 *            can synchronously check whether an operation is in progress.
 *
 * Accepts a boolean or a function `() => boolean`. Pass a function when the
 * blocking condition depends on refs that change synchronously (e.g. before a
 * navigate() call), to ensure the evaluation happens at navigation time rather
 * than at render time.
 */
export function useNavigationBlock(
  shouldBlock: boolean | (() => boolean)
): void {
  // Stable ref always pointing to the latest shouldBlock function.
  // Updated synchronously on every render so it's current when useBlocker
  // evaluates it at navigation time — even before a re-render occurs.
  const shouldBlockFnRef = useRef<() => boolean>(() => false);
  shouldBlockFnRef.current =
    typeof shouldBlock === 'function'
      ? shouldBlock
      : (): boolean => shouldBlock;

  // Derive a plain boolean for the effects below (evaluated at render time).
  const isBlocked =
    typeof shouldBlock === 'function' ? shouldBlock() : shouldBlock;

  // --- Global store (logout, external checks) ---
  useEffect(() => {
    if (!isBlocked) return;

    incrementBlock();
    return (): void => {
      decrementBlock();
    };
  }, [isBlocked]);

  // Ref to the active beforeunload handler so the blocker can remove it before
  // proceeding, preventing a second native dialog when the user already confirmed
  // they want to leave (browsers using the Navigation API intercept reloads via
  // useBlocker AND fire beforeunload when the reload proceeds).
  const beforeUnloadHandlerRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  // --- Browser refresh / close tab ---
  useEffect(() => {
    if (!isBlocked) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };

    beforeUnloadHandlerRef.current = handleBeforeUnload;
    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      beforeUnloadHandlerRef.current = null;
    };
  }, [isBlocked]);

  // --- React Router in-app navigation + browser refresh (Navigation API) ---
  // Use a stable callback that reads from the ref at navigation time, not at
  // render time. This allows callers to bypass the blocker by mutating a ref
  // synchronously before calling navigate(), without needing a re-render.
  const blocker = useBlocker(
    useCallback((): boolean => shouldBlockFnRef.current(), [])
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    const shouldLeave = globalThis.confirm(
      'An operation is in progress. Are you sure you want to leave this page?'
    );

    if (shouldLeave) {
      // Remove the beforeunload listener before proceeding so the native browser
      // dialog does not fire a second time when the reload/navigation completes.
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);
}
